import React from "react";
import { useTranslation } from "react-i18next";
import {
  getYouTubeEmbedUrl,
  getYouTubeWatchUrl,
  isYouTubeUrl,
  normalizeYouTubeUrl,
} from "../utils/youtubeLinks";

interface YoutubeVideosDisplayProps {
  links: string[];
  showTransposeHint?: boolean;
  showLabels?: boolean;
}

export const YoutubeVideosDisplay: React.FC<YoutubeVideosDisplayProps> = ({
  links,
  showTransposeHint = false,
  showLabels = true,
}) => {
  const { t } = useTranslation();

  const displayLinks = links
    .map(normalizeYouTubeUrl)
    .filter((link) => link && isYouTubeUrl(link));

  if (displayLinks.length === 0) return null;

  return (
    <div className="space-y-5">
      {displayLinks.map((link, index) => {
        const embedUrl = getYouTubeEmbedUrl(link);
        if (!embedUrl) return null;

        return (
          <div key={`${link}-${index}`} className="space-y-3">
            {showLabels && displayLinks.length > 1 && (
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {t("video") || "Video"} {index + 1}
              </p>
            )}
            <div className="relative w-full aspect-video overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-700">
              <iframe
                width="100%"
                height="100%"
                className="absolute inset-0 w-full h-full"
                src={embedUrl}
                title={`YouTube video ${index + 1}`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <a
              href={getYouTubeWatchUrl(link)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#5932EA] hover:bg-[#5932EA]/10 dark:hover:bg-[#5932EA]/20 px-3 py-2 rounded-lg transition-colors"
            >
              {t("openInYouTube") || "Open in YouTube"}
            </a>
          </div>
        );
      })}
      {showTransposeHint && (
        <p className="text-xs text-gray-500 dark:text-gray-400 pt-1">
          <a
            href="https://transpose.video/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#5932EA] hover:text-[#4a28c7] dark:hover:text-[#7C3AED]"
          >
            {t("transposeVideoHint") ||
              "Use the Transpose extension to change pitch & speed while practicing"}
          </a>
        </p>
      )}
    </div>
  );
};
